/**
 * The owner profile's "Net payout" column (COL-510): what the owner receives
 * from an offset invoice's month, read two ways.
 *
 *   - Invoice schedule → the LIVE net payout the Owners payouts view already
 *     tracks (`livePayoutEur` on the owner's payout group): the rent collected
 *     so far, less the fee. The DB keeps an open offset invoice's fee in step
 *     with that rent, so the number here and the invoice next to it agree.
 *   - Invoice history → the amount the payout confirmation RECORDED
 *     (`metadata.net_payout_cents`, saved by confirmOffset in the same update
 *     that marks the invoice paid, COL-516), and "—" where none was recorded:
 *     every invoice confirmed before that, and any confirmed where no live
 *     payout figure was available. The invoice's own fee_basis snapshot is
 *     deliberately NOT read as a fallback: it covers only the units the invoice
 *     BILLS. The generator drops a unit whose fee is 0,00 €, yet CoLiviq still
 *     collects and pays out that unit's rent — so the snapshot would understate
 *     a real payout (a 1.900 € payout reading 900 €) under a heading that says
 *     "Net payout". A missing figure is never estimated, and a wrong one is
 *     worse than a dash.
 *
 * Only an OFFSET invoice has a payout. A transfer invoice is one the owner pays
 * (owner_collects), so CoLiviq never holds rent to pay out and its row reads
 * "—", as does a forecast row and any invoice this file cannot vouch for. A
 * missing figure is never estimated.
 *
 * Display-only: nothing here changes how a fee or a payout is calculated.
 */
import {
  isOffsetInvoice,
  OFFSET_NET_PAYOUT_METADATA_KEY,
  type InvoiceSettlement,
} from "@/types/billing";
import {
  closedInvoiceSliceCents,
  type OwnerPayoutGroup,
} from "@/lib/owner-payouts";

/** The invoice fields these helpers read. */
export type NetPayoutInvoice = {
  id: string;
  status: string;
  /** Read-time status; wins over `status` like everywhere else in the tab. */
  displayStatus?: string;
  settlement?: InvoiceSettlement | null;
  property_id?: number | string | null;
  apartment_id?: number | string | null;
  /** First day of the covered month (YYYY-MM-01). */
  period_month: string;
  /** The fee — frozen once the invoice closes. */
  amount_cents: number;
  sent_at?: string | null;
  /** When the invoice was marked paid. */
  paid_at?: string | null;
  fee_basis?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  /** A schedule forecast, not a real invoice. */
  __projected?: boolean;
};

type Eur = number | string | null | undefined;

const isPaid = (inv: NetPayoutInvoice) =>
  (inv.displayStatus ?? inv.status) === "paid";

/**
 * A closed invoice: emailed, or a cancel-and-reissue replacement (which stands
 * in for an invoice that was sent). Its amount no longer follows collected
 * rent. The same test offsetChainFigures uses to tell the month's open invoice
 * from one waiting on its payout.
 */
const isClosed = (inv: NetPayoutInvoice) =>
  !!inv.sent_at || !!inv.metadata?.replaces_invoice_id;

/**
 * The payout a closed invoice's own snapshot records: the rent slice it
 * settles, less its fee. NULL when the snapshot holds no collected rent at all
 * — an offset invoice confirmed before the collected-based fee (COL-474)
 * recorded none, so there is nothing to read.
 *
 * The slice covers only the units the invoice BILLS, which is why this is not
 * a payout figure in its own right (see the file header). It is used for one
 * thing: splitting a group's live payout — a number that IS complete — between
 * the month's closed invoice and the open remainder beside it.
 */
function snapshotNetPayoutCents(inv: NetPayoutInvoice): number | null {
  const basis = inv.fee_basis ?? {};
  if (
    basis.collected_since_close_eur == null &&
    basis.collected_rent_eur == null
  ) {
    return null;
  }
  const slice = closedInvoiceSliceCents({
    collected_since_close_eur: basis.collected_since_close_eur as Eur,
    collected_rent_eur: basis.collected_rent_eur as Eur,
    prior_collected_rent_eur: basis.prior_collected_rent_eur as Eur,
  });
  return Math.max(slice - inv.amount_cents, 0);
}

/**
 * What the payout confirmation of a PAID offset invoice recorded as paid out, in
 * cents; NULL for anything else — a transfer invoice, a forecast, an invoice
 * that is not paid, or a paid one whose confirmation recorded no amount.
 *
 * The amount counts only if the confirmation that wrote it is the one that
 * closed the invoice: confirmOffset stamps `offset_confirmed_at` with the very
 * instant it sets `paid_at`. An invoice that was unmarked and then settled some
 * other way keeps the old key in its metadata but gets a new `paid_at`, so the
 * stale amount reads "—" instead of passing for the new settlement's payout.
 */
export function recordedNetPayoutCents(inv: NetPayoutInvoice): number | null {
  if (inv.__projected || !isOffsetInvoice(inv) || !isPaid(inv)) return null;
  const recorded = inv.metadata?.[OFFSET_NET_PAYOUT_METADATA_KEY];
  if (
    typeof recorded !== "number" ||
    !Number.isInteger(recorded) ||
    recorded < 0
  ) {
    return null;
  }
  const paidAt = Date.parse(inv.paid_at ?? "");
  const confirmedAt = Date.parse(
    String(inv.metadata?.offset_confirmed_at ?? "")
  );
  return Number.isFinite(paidAt) && paidAt === confirmedAt ? recorded : null;
}

/**
 * The live net payout for each unpaid offset invoice that has a payout group, in
 * cents, keyed by invoice id. Invoices absent from the map read "—".
 *
 * A payout group is one property's payout for ONE month: this month's, or an
 * earlier month still open (COL-515, the Owners list's carried-over band). Each
 * group is matched to that month's real per-property offset invoices; a
 * forecast, a future month, or an older month with no group left to show has no
 * live figure. `payoutGroups` is undefined where the caller has none to give (an
 * owner opened from a screen other than the Owners list) and empty on a
 * degraded read — both leave the map empty rather than guess.
 *
 * A group's live payout is one transfer, and it can stand behind two unpaid
 * invoices: one emailed early plus the remainder opened at that send. The
 * closed one keeps its own frozen figure and the open one carries the rest, so
 * the rows add up to the group's payout instead of each repeating it.
 */
export function liveNetPayoutCentsByInvoiceId(
  invoices: readonly NetPayoutInvoice[],
  payoutGroups: readonly OwnerPayoutGroup[] | undefined
): Map<string, number> {
  const out = new Map<string, number>();
  for (const { group, rows } of unpaidOffsetRowsByGroup(
    invoices,
    payoutGroups
  )) {
    const totalCents = Math.round(group.livePayoutEur * 100);
    if (rows.length === 1) {
      out.set(rows[0].id, totalCents);
      continue;
    }

    let closedCents = 0;
    for (const inv of rows.filter(isClosed)) {
      const own = snapshotNetPayoutCents(inv);
      if (own === null) continue;
      out.set(inv.id, own);
      closedCents += own;
    }
    // At most one invoice of a month is open at a time; if the data ever says
    // otherwise, the remainder cannot be attributed, so none is shown.
    const open = rows.filter((inv) => !isClosed(inv));
    if (open.length === 1) {
      out.set(open[0].id, Math.max(totalCents - closedCents, 0));
    }
  }
  return out;
}

/**
 * The net payout a confirmation of each invoice can RECORD (COL-516), in cents,
 * keyed by invoice id: the group's live payout, for an invoice that is the only
 * one of its property-month still waiting to be paid out. Then that figure IS
 * the payout this confirmation makes.
 *
 * Per invoice, not per calendar month: whichever invoice the action is
 * triggered on is the one whose payout is recorded — a full month, an early
 * payout's remainder, or an invoice carried over from an earlier month (the
 * Owners list's carried-over band, COL-515). Every such month has its own
 * payout group, and each group is matched on its own `periodMonth`.
 *
 * Narrower than the schedule column on purpose:
 *   - Where an invoice emailed early and its remainder are both waiting, the
 *     column splits one transfer between two rows for display, but the closed
 *     row's share comes from its fee_basis snapshot (billed units only) — fine
 *     for a split that adds up to the right total, not something to save as
 *     "what was paid out" for either invoice. Those confirmations record
 *     nothing, and the history reads "—".
 *   - The group and `invoices` come from different queries, and after a
 *     confirm or an unmark the invoice list refetches before the owners list
 *     recomputes its payout groups. A count of what each side sees is not
 *     enough to catch that: in a chain A → B → C (B emailed early, C its
 *     remainder), confirming B leaves C as the list's only unpaid row while
 *     the group is still a step behind and holds the whole B + C transfer —
 *     one paid invoice either way, so a count agrees while the two sides mean
 *     different invoices, and B + C would be saved as C's payout. Permanently:
 *     unlike the column, this figure is written to the invoice.
 *
 *     So the two sides are compared by IDENTITY: the group names the invoices
 *     its `livePayoutEur` is the payout for (`unpaidInvoiceIds`), and a figure
 *     is offered only where that set is exactly the one invoice the list has
 *     waiting. A group that names none — a legacy consolidated document in
 *     play, or ids the caller did not select — records nothing rather than a
 *     guess, which is also what a stale group does until it catches up.
 *
 * `invoices` must include the owner's PAID invoices, not just the schedule.
 */
export function payoutToRecordCentsByInvoiceId(
  invoices: readonly NetPayoutInvoice[],
  payoutGroups: readonly OwnerPayoutGroup[] | undefined
): Map<string, number> {
  const out = new Map<string, number>();
  for (const { group, rows } of unpaidOffsetRowsByGroup(
    invoices,
    payoutGroups
  )) {
    if (rows.length !== 1) continue;
    // `?? []` for a group that predates the field or reaches this component
    // without it: unknown, which withholds the figure — never "nothing is
    // waiting", which would offer one.
    const named = group.unpaidInvoiceIds ?? [];
    if (named.length !== 1 || named[0] !== rows[0].id) continue;
    out.set(rows[0].id, Math.round(group.livePayoutEur * 100));
  }
  return out;
}

/**
 * Each payout group paired with the unpaid per-property offset invoices of ITS
 * OWN month that belong to it (groups with no unpaid invoice are left out). A
 * group is one property's payout for one month — this month's, or an earlier
 * month still open (COL-515) — so a forecast, a future month, or a month with
 * no group left to show matches nothing. `payoutGroups` is undefined where the
 * caller has none to give (an owner opened from a screen other than the Owners
 * list) and empty on a degraded read — both match nothing rather than guess.
 */
function unpaidOffsetRowsByGroup(
  invoices: readonly NetPayoutInvoice[],
  payoutGroups: readonly OwnerPayoutGroup[] | undefined
): { group: OwnerPayoutGroup; rows: NetPayoutInvoice[] }[] {
  if (!payoutGroups?.length) return [];
  const out: { group: OwnerPayoutGroup; rows: NetPayoutInvoice[] }[] = [];
  for (const group of payoutGroups) {
    const rows = invoices.filter(
      (inv) =>
        !inv.__projected &&
        isOffsetInvoice(inv) &&
        !isPaid(inv) &&
        inv.status !== "canceled" &&
        // The per-property document the group is built from; a legacy
        // per-apartment or portfolio row bills a different slice.
        inv.apartment_id == null &&
        inv.property_id != null &&
        String(inv.property_id) === group.propertyId &&
        String(inv.period_month).slice(0, 7) === group.periodMonth
    );
    if (rows.length > 0) out.push({ group, rows });
  }
  return out;
}
